import type { ApiClient, Query } from '@saas/ui';
import type {
  ChannelListItem,
  ChannelListQuery,
  ChatConversationExtras,
  ChatCallJoinResult,
  ChatCallSummary,
  ChatCallKind,
  ChatNotifyLevel,
  ChatSidebar,
  ChatUserSettings,
  ChatWebhookSecret,
  ChatWebhookSummary,
  CreateChatChannelInput,
  DirectorySyncResult,
  DisplayAsChannelResult,
  DmResult,
  ExternalGuestInviteResult,
  LinkedTaskInput,
  SaveMessageInput,
  SavedItem,
  SavedListQuery,
  SearchQuery,
  SearchResult,
  SubmitWorkflowInput,
  ThreadItem,
  ThreadsQuery,
  UpdateChatChannelInput,
  UpdateChatUserSettingsInput,
} from '../api/chat.dto.js';

/**
 * chat の HTTP 契約（ブラウザ側）。型は api/ の DTO（Date は JSON では ISO 文字列で届く）。
 * メッセージの投稿 / 既読 / 返信 / リアクション / 退出は conversation の conversationApi（@saas/module-conversation/ui）を使う。
 */
export const chatKeys = {
  all: () => ['chat'] as const,
  sidebar: () => ['chat', 'sidebar'] as const,
  capabilities: () => ['chat', 'capabilities'] as const,
  channels: (q: Partial<ChannelListQuery>) => ['chat', 'channels', q] as const,
  extras: (id: string) => ['chat', 'extras', id] as const,
  call: (id: string) => ['chat', 'call', id] as const,
  pins: (id: string) => ['chat', 'pins', id] as const,
  threads: (q: Partial<ThreadsQuery>) => ['chat', 'threads', q] as const,
  saved: (q: Partial<SavedListQuery>) => ['chat', 'saved', q] as const,
  search: (q: Partial<SearchQuery>) => ['chat', 'search', q] as const,
  settings: () => ['chat', 'settings'] as const,
  webhooks: (id: string) => ['chat', 'webhooks', id] as const,
};

export interface ChatCapabilities {
  workflow: boolean;
  calls: boolean;
}

export const chatApi = {
  sidebar: (api: ApiClient) => api.get<ChatSidebar>('/chat/sidebar'),
  capabilities: (api: ApiClient) => api.get<ChatCapabilities>('/chat/capabilities'),
  openDm: (api: ApiClient, userId: string) => api.post<DmResult>('/chat/dm', { userId }),
  channels: (api: ApiClient, q: Partial<ChannelListQuery>) => api.get<ChannelListItem[]>('/chat/channels', q as Query),
  createChannel: (api: ApiClient, body: CreateChatChannelInput) => api.post<ChannelListItem>('/chat/channels', body),
  updateChannel: (api: ApiClient, id: string, body: UpdateChatChannelInput) => api.patch<ChannelListItem>(`/chat/channels/${id}`, body),
  join: (api: ApiClient, id: string) => api.post<ChannelListItem>(`/chat/channels/${id}/join`),
  invite: (api: ApiClient, id: string, userIds: string[]) => api.post<ChatConversationExtras>(`/chat/channels/${id}/invite`, { userIds }),
  inviteExternal: (api: ApiClient, id: string, body: { email: string; displayName: string }) => api.post<ExternalGuestInviteResult>(`/chat/channels/${id}/invite-external`, body),
  archive: (api: ApiClient, id: string) => api.post<ChannelListItem>(`/chat/channels/${id}/archive`),
  webhooks: (api: ApiClient, id: string) => api.get<ChatWebhookSummary[]>(`/chat/channels/${id}/webhooks`),
  createWebhook: (api: ApiClient, id: string, name: string) => api.post<ChatWebhookSecret>(`/chat/channels/${id}/webhooks`, { name }),
  rotateWebhook: (api: ApiClient, id: string, webhookId: string) => api.post<ChatWebhookSecret>(`/chat/channels/${id}/webhooks/${webhookId}/rotate`),
  revokeWebhook: (api: ApiClient, id: string, webhookId: string) => api.del<{ ok: true }>(`/chat/channels/${id}/webhooks/${webhookId}`),
  sync: (api: ApiClient) => api.post<DirectorySyncResult>('/chat/channels/sync'),
  extras: (api: ApiClient, id: string) => api.get<ChatConversationExtras>(`/chat/conversations/${id}`),
  /** 入力中の合図（在席の「チャット中」・gap-comms）。入力欄が 1 分に 1 回まで送る。失敗は無視してよい */
  typing: (api: ApiClient, id: string) => api.post<{ inChat: boolean }>(`/chat/conversations/${id}/typing`),
  activeCall: (api: ApiClient, id: string) => api.get<ChatCallSummary | null>(`/chat/conversations/${id}/call`),
  startCall: (api: ApiClient, id: string, kind: ChatCallKind) => api.post<ChatCallJoinResult>(`/chat/conversations/${id}/calls`, { kind }),
  joinCall: (api: ApiClient, id: string, meetingId: string) => api.post<ChatCallJoinResult>(`/chat/conversations/${id}/calls/${meetingId}/join`),
  declineCall: (api: ApiClient, id: string, meetingId: string) => api.post<ChatCallSummary>(`/chat/conversations/${id}/calls/${meetingId}/decline`),
  endCall: (api: ApiClient, id: string, meetingId: string) => api.post<ChatCallSummary>(`/chat/conversations/${id}/calls/${meetingId}/end`),
  /** 本人だけ退出（通話は続く。在席の「通話中」を外す） */
  leaveCall: (api: ApiClient, id: string, meetingId: string, opts: { keepalive?: boolean } = {}) =>
    api.post<{ ok: true; left: boolean }>(`/chat/conversations/${id}/calls/${meetingId}/leave`, undefined, opts.keepalive ? { keepalive: true } : {}),
  setNotify: (api: ApiClient, id: string, level: ChatNotifyLevel | null) => api.put<ChatConversationExtras>(`/chat/conversations/${id}/notify`, { level }),
  /** レコード会話をチャンネル表示に昇格 / 解除（§12.5-2・§J-7） */
  setDisplayAsChannel: (api: ApiClient, id: string, displayAsChannel: boolean) => api.put<DisplayAsChannelResult>(`/chat/conversations/${id}/display-as-channel`, { displayAsChannel }),
  pins: (api: ApiClient, id: string) => api.get<SavedItem[]>(`/chat/conversations/${id}/pins`),
  threads: (api: ApiClient, q: Partial<ThreadsQuery>) => api.get<ThreadItem[]>('/chat/threads', q as Query),
  saved: (api: ApiClient, q: Partial<SavedListQuery>) => api.get<SavedItem[]>('/chat/saved', q as Query),
  save: (api: ApiClient, messageId: string, body: SaveMessageInput) => api.post<SavedItem>(`/chat/messages/${messageId}/saved`, body),
  done: (api: ApiClient, savedId: string) => api.post<{ ok: true }>(`/chat/saved/${savedId}/done`),
  undone: (api: ApiClient, savedId: string) => api.post<{ ok: true }>(`/chat/saved/${savedId}/undone`),
  removeSaved: (api: ApiClient, savedId: string) => api.del<{ ok: true }>(`/chat/saved/${savedId}`),
  submitWorkflow: (api: ApiClient, messageId: string, body: SubmitWorkflowInput) => api.post<{ requestId: string; requestNo: string }>(`/chat/messages/${messageId}/workflow`, body),
  /** 会話に残るシステム投稿の ID。テナントがその種類を OFF にしていれば null（タスク自体は作られている） */
  linkTask: (api: ApiClient, messageId: string, body: LinkedTaskInput) => api.post<{ messageId: string | null }>(`/chat/messages/${messageId}/linked-task`, body),
  search: (api: ApiClient, q: SearchQuery) => api.get<SearchResult>('/chat/search', q as unknown as Query),
  settings: (api: ApiClient) => api.get<ChatUserSettings>('/chat/settings'),
  updateSettings: (api: ApiClient, body: UpdateChatUserSettingsInput) => api.patch<ChatUserSettings>('/chat/settings', body),
};

/** 会話ページ（通知のリンク先と同じ形） */
export const chatConversationPath = (conversationId: string, opts: { message?: string | null; thread?: string | null } = {}): string => {
  const sp = new URLSearchParams();
  if (opts.message) sp.set('message', opts.message);
  if (opts.thread) sp.set('thread', opts.thread);
  const q = sp.toString();
  return `/chat/c/${conversationId}${q ? `?${q}` : ''}`;
};
